import { useState, useEffect, useMemo } from 'react';
import { getDeviceDailyMetrics, getDeviceData } from '../utils/api';

// Map one device's API rows into the [timestampMs, value] pairs ECharts wants.
// Mirrors useChartData's mapping, including the missing-reading sentinels: the
// API marks a missing battery with -1 (0% is a real, flat cell) and a missing
// rssi with >= 0 (0 dBm is physically unreachable). Both become null so ECharts
// draws a gap.
function toSeries(rows) {
	const at = (d) => new Date(d.datetime).getTime();
	return {
		temp_values: rows.map((d) => [at(d), d.temp_value]),
		moist_values: rows.map((d) => [at(d), d.moist_value]),
		light_values: rows.map((d) => [at(d), d.light_value]),
		nutrition_values: rows.map((d) => [at(d), d.nutrition_value]),
		battery_values: rows.map((d) => [at(d), d.battery_value < 0 ? null : d.battery_value]),
		rssi_values: rows.map((d) => [at(d), d.rssi_value >= 0 ? null : d.rssi_value]),
	};
}

// useDevicesChartData is the many-device sibling of useChartData: it fetches
// several devices' measurements at once so they can share a single chart, rather
// than forcing one hook instance (and one chart) per device.
//
// mode: 'raw' (raw measurements, last 7 days) or 'daily' (daily averages and
// daily light integrals over the last year). Note light_value means different
// things per mode: instantaneous lux for 'raw', lux·h/day (DLI) for 'daily' —
// only the latter is comparable to the plant profile's light bands.
//
// Returns byDevice keyed by device id. A device whose fetch fails is simply
// absent from byDevice, so one bad device does not blank the whole chart.
export const useDevicesChartData = (deviceIds, mode = 'daily') => {
	const [byDevice, setByDevice] = useState({});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// deviceIds is typically a fresh array every render (spread from a Set), so
	// key the effect on its contents rather than its identity.
	const key = useMemo(() => [...deviceIds].sort((a, b) => a - b).join(','), [deviceIds]);

	useEffect(() => {
		const ids = key === '' ? [] : key.split(',').map(Number);
		if (ids.length === 0) {
			setByDevice({});
			setLoading(false);
			setError(null);
			return;
		}

		let active = true;
		setLoading(true);
		setError(null);

		const fetchOne = async (id) => {
			const response = mode === 'daily' ? await getDeviceDailyMetrics(id) : await getDeviceData(id);
			if (!response.ok) throw new Error(`Failed to fetch measurements for device ${id}`);
			const json = await response.json();
			return [id, toSeries(json.data || [])];
		};

		Promise.allSettled(ids.map(fetchOne))
			.then((results) => {
				if (!active) return;
				const next = {};
				let failed = 0;
				for (const r of results) {
					if (r.status === 'fulfilled') next[r.value[0]] = r.value[1];
					else failed++;
				}
				setByDevice(next);
				setError(failed > 0 ? `Could not load measurements for ${failed} of ${ids.length} devices` : null);
			})
			.finally(() => active && setLoading(false));

		return () => {
			active = false;
		};
	}, [key, mode]);

	return { byDevice, loading, error };
};

export default useDevicesChartData;
